export const PAGE_SHOW_EVENT = 'pageshow';

export const getBrowserVisibilityProp = (): string | undefined => {
  if (typeof document.hidden !== 'undefined') return 'visibilitychange';

  if (
    typeof (document as Document & { msHidden?: boolean }).msHidden !==
    'undefined'
  )
    return 'msvisibilitychange';

  if (
    typeof (document as Document & { webkitHidden?: boolean }).webkitHidden !==
    'undefined'
  )
    return 'webkitvisibilitychange';

  return undefined;
};

const getBrowserDocumentHiddenProp = (): string => {
  if (typeof document.hidden !== 'undefined') return 'hidden';

  if (
    typeof (document as Document & { msHidden?: boolean }).msHidden !==
    'undefined'
  )
    return 'msHidden';

  return 'webkitHidden';
};

export const isDocumentVisible = (): boolean => {
  const hiddenProp = getBrowserDocumentHiddenProp();
  return !(document as Document & Record<string, boolean>)[hiddenProp];
};

/**
 * Runs `onActive` when the user returns to the page (tab visible or window focused).
 * Covers tab switches and clicking back from DevTools after editing localStorage.
 */
export const attachPageActiveListener = (
  onActive: () => void
): (() => void) => {
  const handleActive = () => {
    if (!isDocumentVisible()) return;

    onActive();
  };

  const visibilityChange = getBrowserVisibilityProp();

  if (visibilityChange)
    window.addEventListener(visibilityChange, handleActive, false);

  window.addEventListener('focus', handleActive);

  return () => {
    if (visibilityChange)
      window.removeEventListener(visibilityChange, handleActive);

    window.removeEventListener('focus', handleActive);
  };
};

/**
 * Runs callbacks when the document becomes visible or hidden (tab switch, minimize).
 */
export const attachDocumentVisibilityListener = (
  onVisible: () => void,
  onHidden: () => void
): (() => void) => {
  const handleVisibilityChange = () => {
    if (isDocumentVisible()) {
      onVisible();
      return;
    }

    onHidden();
  };

  const visibilityChange = getBrowserVisibilityProp();

  if (visibilityChange) {
    window.addEventListener(visibilityChange, handleVisibilityChange, false);
  }

  return () => {
    if (visibilityChange) {
      window.removeEventListener(visibilityChange, handleVisibilityChange);
    }
  };
};
